import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Settings, LogOut, Sun, Moon } from 'lucide-react'
import { useAuth0 } from '@auth0/auth0-react'
import { cn } from '@/lib/utils'
import { useTheme } from '@/lib/theme'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { SidebarTooltip } from './SidebarTooltip'
import { SidebarModeSelector } from './SidebarModeSelector'
import type { SidebarMode } from '../../hooks/useSidebarState'
import type { LucideIcon } from 'lucide-react'

interface SidebarUserFooterProps {
  isExpanded: boolean
  mode: SidebarMode
  onModeChange: (mode: SidebarMode) => void
  rol?: string
}

interface IconButtonProps {
  icon: LucideIcon
  label: string
  onClick?: () => void
  href?: string
  isExpanded: boolean
}

function IconButton({ icon: Icon, label, onClick, href, isExpanded }: IconButtonProps) {
  const buttonContent = href ? (
    <NavLink to={href}>
      <Icon className="h-4 w-4" />
    </NavLink>
  ) : (
    <Icon className="h-4 w-4" />
  )

  const button = (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-muted-foreground hover:text-foreground"
      onClick={onClick}
      asChild={!!href}
    >
      {buttonContent}
    </Button>
  )

  if (!isExpanded) {
    return (
      <SidebarTooltip content={label} side="top">
        {button}
      </SidebarTooltip>
    )
  }

  return button
}

export function SidebarUserFooter({
  isExpanded,
  mode,
  onModeChange,
  rol,
}: SidebarUserFooterProps) {
  const [showLogoutDialog, setShowLogoutDialog] = useState(false)
  const { user, logout } = useAuth0()
  const { resolvedTheme, setTheme } = useTheme()

  const toggleTheme = () => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } })
  }

  return (
    <>
      <div className="border-t p-3">
        {/* Usuario */}
        <div className={cn('flex items-center gap-2 mb-2', !isExpanded && 'justify-center')}>
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name || 'Usuario'}
              className="h-8 w-8 rounded-full shrink-0"
            />
          ) : (
            <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0">
              <span className="text-sm font-medium">{user?.name?.charAt(0) || 'U'}</span>
            </div>
          )}

          {isExpanded && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'Usuario'}</p>
              {rol && (
                <Badge variant="outline" className="text-[10px] px-1 py-0">
                  {rol}
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Controles */}
        <div className={cn('flex gap-1', isExpanded ? 'justify-between' : 'flex-col items-center')}>
          <div className="flex gap-1">
            <IconButton
              icon={Settings}
              label="Configuracion"
              href="/configuracion"
              isExpanded={isExpanded}
            />
            <IconButton
              icon={resolvedTheme === 'dark' ? Sun : Moon}
              label={resolvedTheme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
              onClick={toggleTheme}
              isExpanded={isExpanded}
            />
          </div>

          <div className="flex gap-1">
            <SidebarModeSelector mode={mode} onModeChange={onModeChange} isExpanded={isExpanded} />
            <IconButton
              icon={LogOut}
              label="Cerrar sesion"
              onClick={() => setShowLogoutDialog(true)}
              isExpanded={isExpanded}
            />
          </div>
        </div>
      </div>

      {/* Dialog de confirmación de logout */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent className="max-w-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cerrar sesión?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a salir de tu cuenta. Podrás volver a iniciar sesión en cualquier momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleLogout}>
              Cerrar sesión
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
